import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../../../../shared/types/ApiCallType'
import { GlobalSortDirectionEnum } from '../../../../shared/types/GlobalSortDirectionEnum'
import { type RecurringPayment } from '../../../../shared/proxies/recurringPaymentProxy'
import { DEFAULT_LIMIT } from '../../../../shared/utils/apiUtils'
import { RecurringPaymentStatus } from '../../../../shared/types/GlobalRecurringPaymentsStatus'

export enum TableColumnsEnum {
  RecurringPaymentId = 'id',
  CompanyName = 'customer.company_name',
  CustomerName = 'recurring_payments.last_name',
  InvoiceNumber = 'invoice_number',
  Status = 'status',
  BillingCycle = 'billing_cycle',
  Amount = 'amount_cents',
  LastUpdatedDate = 'last_payment_date',
  NextPaymentDate = 'next_payment_date',

}

export interface Pagination {
  totalCount: number
  limit: number
  offset: number
}

export interface Filters {
  endDate: Date | null
  offset: number
  search: string
  sortColumn: TableColumnsEnum
  sortDirection: GlobalSortDirectionEnum
  startDate: Date | null
  statuses: readonly RecurringPaymentStatus[]
}

export interface RecurringPaymentsProxyType {
  apiCall: ApiCallType
  filters: Filters
  recurringPayments: RecurringPayment[]
  exportRecurringPayments: RecurringPayment[]
  pagination: Pagination
  hasMore: boolean
  hasAppliedFilters: boolean
}

const baseProxy = proxy({
  apiCall: {
    error: null,
    loading: false
  },
  exportRecurringPayments: [],
  filters: {
    endDate: null,
    offset: 0,
    search: '',
    sortColumn: TableColumnsEnum.NextPaymentDate,
    sortDirection: GlobalSortDirectionEnum.Descending,
    startDate: null,
    statuses: [RecurringPaymentStatus.Paused, RecurringPaymentStatus.Active, RecurringPaymentStatus.Errored]
  },
  pagination: {
    limit: DEFAULT_LIMIT,
    offset: 0,
    totalCount: 0
  },
  recurringPayments: []
})

export const recurringPaymentsProxy = derive({
  hasAppliedFilters: get => {
    const { filters } = get(baseProxy)
    return Boolean(
      filters.endDate ||
      filters.search.length > 0 ||
      filters.startDate
    )
  },
  hasMore: get => {
    const { recurringPayments, pagination } = get(baseProxy)
    const totalCount = pagination?.totalCount ?? 0
    return recurringPayments.length < totalCount
  }
}, { proxy: baseProxy }) as RecurringPaymentsProxyType

devtools(recurringPaymentsProxy, { name: 'recurringPaymentsProxy' })
